(() => {
  if (customElements.get('nd-product-details')) {
    return;
  }

  const SECTION_HEADING = /^\s*(composition|care(?:\s+instructions?)?)\s*:?\s*/i;
  const DRY_CLEAN_CARE = /^\s*dry\s+clean\s+only\b/i;
  const OTHER_HEADING = /^\s*(description|details|specifications?|features?|country\s+of\s+origin|manufacturer(?:\s+info)?|packaging|shipping(?:\s+information)?|size(?:\s+(?:and\s+fit|guide))?|materials?)\s*:?\s*$/i;
  const LEGACY_LABEL = /\b(Lining\s+(?:and|&)\s+Sole|Lining\s+Cushion|Outer\s+Material|Inner\s+Material|Heel\s+Height|Upper|Back|Lining|Sole|Closure|Material)\s*(?::|[-–—])\s*/gi;
  const CONCATENATED_LEGACY_LABEL = /([A-Za-z0-9])(?=(?:Lining\s+(?:and|&)\s+Sole|Lining\s+Cushion|Outer\s+Material|Inner\s+Material|Heel\s+Height|Upper|Back|Lining|Sole|Closure|Material)\s*(?::|[-–—]))/gi;
  const BLOCK_TAGS = new Set(['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'UL', 'OL', 'TABLE', 'BLOCKQUOTE']);

  const normalizedText = (node) => node.textContent.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();

  const collectUnits = (root) => {
    const units = [];

    const visit = (parent) => {
      [...parent.childNodes].forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          if (normalizedText(node)) {
            units.push(node);
          }
          return;
        }

        if (node.nodeType !== Node.ELEMENT_NODE) {
          return;
        }

        if (BLOCK_TAGS.has(node.tagName)) {
          units.push(node);
          return;
        }

        const containsBlockChildren = [...node.children].some((child) => BLOCK_TAGS.has(child.tagName) || child.tagName === 'DIV');

        if (containsBlockChildren) {
          visit(node);
        } else if (normalizedText(node) || node.querySelector('img, video, table')) {
          units.push(node);
        }
      });
    };

    visit(root);
    return units;
  };

  const sectionType = (node) => {
    const match = normalizedText(node).match(SECTION_HEADING);

    if (!match) {
      return null;
    }

    return match[1].toLowerCase().startsWith('care') ? 'care' : 'composition';
  };

  const removeLeadingLabel = (node) => {
    const clone = node.cloneNode(true);

    if (clone.nodeType === Node.TEXT_NODE) {
      clone.data = clone.data.replace(SECTION_HEADING, '');
      return clone;
    }

    const walker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    let combinedText = '';
    let textNode;

    while ((textNode = walker.nextNode())) {
      textNodes.push(textNode);
      combinedText += textNode.data;
    }

    const match = combinedText.match(SECTION_HEADING);

    if (!match) {
      return clone;
    }

    let charactersToRemove = match[0].length;

    textNodes.forEach((currentNode) => {
      if (charactersToRemove <= 0) {
        return;
      }

      if (charactersToRemove >= currentNode.data.length) {
        charactersToRemove -= currentNode.data.length;
        currentNode.data = '';
      } else {
        currentNode.data = currentNode.data.slice(charactersToRemove);
        charactersToRemove = 0;
      }
    });

    clone.querySelectorAll('strong, b, em, span').forEach((element) => {
      if (!normalizedText(element) && !element.querySelector('img, video')) {
        element.remove();
      }
    });

    while (clone.firstChild && ((clone.firstChild.nodeType === Node.TEXT_NODE && !normalizedText(clone.firstChild)) || clone.firstChild.nodeName === 'BR')) {
      clone.firstChild.remove();
    }

    return clone;
  };

  const hasContent = (fragment) => normalizedText(fragment) !== '' || Boolean(fragment.querySelector('img, video, table, ul, ol'));

  const cloneRange = (units, start, end, removeHeading) => {
    const fragment = document.createDocumentFragment();

    units.slice(start, end).forEach((unit, index) => {
      fragment.append(index === 0 && removeHeading ? removeLeadingLabel(unit) : unit.cloneNode(true));
    });

    return fragment;
  };

  const nextBoundary = (units, start, includeDryClean) => {
    for (let index = start + 1; index < units.length; index += 1) {
      const text = normalizedText(units[index]);

      if (sectionType(units[index]) || OTHER_HEADING.test(text) || includeDryClean && DRY_CLEAN_CARE.test(text)) {
        return index;
      }
    }

    return units.length;
  };

  const extractSections = (source) => {
    const container = document.createElement('div');
    container.append(source.content.cloneNode(true));
    const units = collectUnits(container);
    const compositionIndex = units.findIndex((unit) => sectionType(unit) === 'composition');
    const careIndex = units.findIndex((unit) => sectionType(unit) === 'care');
    const dryCleanIndex = careIndex === -1
      ? units.findIndex((unit, index) => index > compositionIndex && DRY_CLEAN_CARE.test(normalizedText(unit)))
      : -1;
    const result = {};

    if (compositionIndex !== -1) {
      const compositionEnd = nextBoundary(units, compositionIndex, true);
      const composition = cloneRange(units, compositionIndex, compositionEnd, true);

      if (hasContent(composition)) {
        result.composition = composition;
      }
    }

    const resolvedCareIndex = careIndex !== -1 ? careIndex : dryCleanIndex;

    if (resolvedCareIndex !== -1) {
      const careEnd = nextBoundary(units, resolvedCareIndex, false);
      const care = cloneRange(units, resolvedCareIndex, careEnd, careIndex !== -1);

      if (hasContent(care)) {
        result.care = care;
      }
    }

    return result;
  };

  const sourceContent = (source) => source instanceof HTMLTemplateElement ? source.content : source;

  const textWithBreaks = (source) => {
    const container = document.createElement('div');
    container.append(sourceContent(source).cloneNode(true));
    container.querySelectorAll('br').forEach((breakElement) => breakElement.replaceWith(document.createTextNode('\n')));
    container.querySelectorAll('p, div, li, tr, blockquote').forEach((element) => element.append(document.createTextNode('\n')));

    return container.textContent
      .replace(/\u00a0/g, ' ')
      .replace(/[ \t]+/g, ' ')
      .replace(/ *\n */g, '\n')
      .replace(/\n{2,}/g, '\n')
      .trim();
  };

  const formatComposition = (source) => {
    const text = textWithBreaks(source)
      .replace(CONCATENATED_LEGACY_LABEL, '$1\n')
      .trim();
    const matches = [...text.matchAll(LEGACY_LABEL)];

    if (matches.length === 0) {
      return sourceContent(source).cloneNode(true);
    }

    const fragment = document.createDocumentFragment();
    const list = document.createElement('div');
    list.className = 'nd-spec-list';
    list.setAttribute('role', 'list');

    matches.forEach((match, index) => {
      const end = matches[index + 1]?.index ?? text.length;
      const value = text.slice(match.index + match[0].length, end).replace(/\s+/g, ' ').trim();

      if (value) {
        const row = document.createElement('div');
        const key = document.createElement('strong');
        const separator = document.createElement('span');
        const valueElement = document.createElement('span');

        row.className = 'nd-spec-row';
        row.setAttribute('role', 'listitem');
        key.textContent = match[1].replace(/\s+/g, ' ').trim();
        separator.className = 'nd-spec-row__separator';
        separator.textContent = ':';
        separator.setAttribute('aria-hidden', 'true');
        valueElement.textContent = value;
        row.append(key, separator, valueElement);
        list.append(row);
      }
    });

    if (!list.children.length) {
      return sourceContent(source).cloneNode(true);
    }

    fragment.append(list);
    return fragment;
  };

  const formatCare = (source) => {
    const text = textWithBreaks(source);
    const entries = text
      .replace(/([.!?])\s+(?=[A-Z])/g, '$1\n')
      .split(/\n+/)
      .map((entry) => entry.replace(/\s+/g, ' ').trim())
      .filter((entry, index, allEntries) => entry && allEntries.indexOf(entry) === index);

    if (!entries.length) {
      return sourceContent(source).cloneNode(true);
    }

    const fragment = document.createDocumentFragment();
    const list = document.createElement('ul');
    list.className = 'nd-care-list';

    entries.forEach((entry) => {
      const item = document.createElement('li');
      item.textContent = entry;
      list.append(item);
    });

    fragment.append(list);
    return fragment;
  };

  class NeedledustProductDetails extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ndInitialized === 'true') {
        return;
      }

      this.dataset.ndInitialized = 'true';
      this.hydrate();
    }

    hydrate() {
      const descriptionSource = this.querySelector('template[data-nd-description-source]');
      const extracted = descriptionSource ? extractSections(descriptionSource) : {};

      if (extracted.composition) {
        this.showDetail('composition', formatComposition(extracted.composition));
      } else {
        const legacySource = this.querySelector('template[data-nd-legacy-composition]');
        const legacyComposition = legacySource ? formatComposition(legacySource) : null;

        if (legacyComposition && hasContent(legacyComposition)) {
          this.showDetail('composition', legacyComposition);
        }
      }

      if (extracted.care) {
        this.showDetail('care', formatCare(extracted.care));
      }
    }

    showDetail(name, content) {
      const detail = this.querySelector(`[data-nd-detail="${name}"]`);
      const destination = detail?.querySelector('[data-nd-detail-content]');

      if (!detail || !destination) {
        return;
      }

      destination.replaceChildren(content);
      detail.hidden = false;
    }
  }

  customElements.define('nd-product-details', NeedledustProductDetails);
})();
